/**
 * Billing — the billing group of the unified Settings shell.
 *
 * This file no longer renders a standalone page: the Settings shell
 * (settings/SettingsPage.js) owns the breadcrumbs, the grouped left
 * rail and the outlined content panel. What lives here:
 *
 *   * ``BillingSection`` (default export) — the panel content for one
 *     billing sub-section ('overview' | 'usage' | 'activity'
 *     | 'invoices'), always anchored by the BalanceStrip (balance +
 *     plan / renewal meta + Manage billing / Change plan / Top up
 *     actions). The full price list is a dialog ("View pricing" on
 *     Usage & Limits), not a section.
 *
 * Plans are NOT a section — changing plan is an action: the strip's
 * "Change plan" button opens the chooser dialog.
 *
 * Data: one round-trip via GET /v1/billing/snapshot; transactions are
 * paginated separately.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Stack, Chip,
  LinearProgress, Tooltip, IconButton, MenuItem, TextField,
  Table, TableBody, TableCell, TableHead, TableRow,
  Pagination, CircularProgress, Alert, Grid, Dialog, DialogTitle,
  DialogContent, Paper,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import DonutSmallIcon from '@mui/icons-material/DonutSmall';
import SpeedIcon from '@mui/icons-material/Speed';
import MemoryIcon from '@mui/icons-material/Memory';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import RuleIcon from '@mui/icons-material/Rule';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import { InferencesIcon } from '../global/CustomIcons';

import { fetchRequest as fetchSnapshot } from '../../redux/actions/billing/snapshot';
import { fetchRequest as fetchBalance } from '../../redux/actions/billing/balance';
import { fetchRequest as fetchTransactions } from '../../redux/actions/billing/transactions';
import { fetchRequest as fetchSubscription } from '../../redux/actions/billing/subscription';
import { fetchRequest as fetchWorkspaces } from '../../redux/actions/workspaces/list';

import { CMButton, CMButtonOutlinedSmall } from '../../containers/styledComponents';
import { KPICard, Surface, formatNumber } from '../dashboard/components/DashboardPrimitives';

import StripePortalLink from './StripePortalLink';
import {
  describeActivity, FILTER_OPTIONS, formatCredits,
  meterTone, formatLimitUsage, limitPercent, tierLabel,
  isSpendCategory, CATEGORY_COLORS,
} from './billingHelpers';
import { useViewerOrganizations } from '../marketplace/marketplaceUtils';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import TopUpDialog from './TopUpDialog';
import PlansPanel from './PlansPanel';
import PricingTable from './PricingTable';
import InvoicesPanel from './InvoicesPanel';
import SpendChart from './SpendChart';
import CategoryDonut from './CategoryDonut';
import WorkspaceSpendChart from './WorkspaceSpendChart';
import AIOpsPanel from './AIOpsPanel';

const PAGE_SIZE = 25;

const SUBS = ['overview', 'usage', 'activity', 'invoices'];

// Balance health drives the strip tone + header chip: red at zero,
// amber under a fixed floor, green otherwise.
const LOW_BALANCE_FLOOR = 1000;
const balanceHealth = (credits) => {
  if (credits <= 0) return { tone: 'error', label: 'Out of credits' };
  if (credits < LOW_BALANCE_FLOOR) return { tone: 'warning', label: 'Balance low' };
  return { tone: 'success', label: 'Balance' };
};

/* Plan meta line for the BalanceStrip: plan tier · scope · included
   monthly credits · renewal date. Reads the subscription slice loaded
   by BillingSection. */
const fmtShortDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const MODE_LABEL = {
  complimentary: 'complimentary',
  pilot:         'pilot',
  trial:         'trial',
};

const PlanMetaLine = () => {
  const snapshot = useSelector((s) => s.billing_snapshot?.response);
  const subscription = useSelector((s) => s.billing_subscription?.response);
  const balance = snapshot?.balance;
  const plan = snapshot?.plan || {};
  const state = plan.state || {};
  const status = state.status;
  // The tier the user SUBSCRIBED to, not the one they're priced at (an
  // expired individual plan is priced as Free — saying "Free plan" there
  // would hide the lapse).
  const tierName = tierLabel(plan.subscribed_tier || state.tier || plan.name);
  const modeLabel = MODE_LABEL[state.billing_mode] || null;
  const autoRenews = !!(subscription?.auto_renews ?? state.auto_renews);
  const endDate = fmtShortDate(state.expires_at || subscription?.current_period_end || plan.expires_at);
  const includedCredits = subscription?.included_credits;

  let endLabel = null;
  if (status === 'expired' || status === 'suspended') {
    endLabel = `expired ${endDate}`;
  } else if (endDate) {
    // Only a live Stripe subscription actually renews; admin-assigned,
    // complimentary and simulated plans simply END.
    endLabel = autoRenews ? `renews ${endDate}` : `ends ${endDate}`;
  } else if (status === 'active' && !state.expires_at) {
    endLabel = 'no end date';
  }

  return [
    `${[modeLabel, tierName].filter(Boolean).join(' ')} plan`,
    balance?.scope === 'org' ? 'organization account' : 'individual account',
    status === 'active' || status === 'expiring_soon'
      ? (includedCredits != null && includedCredits > 0
        ? `${formatCredits(includedCredits)} credits included monthly`
        : null)
      : null,
    endLabel,
  ].filter(Boolean).join(' · ');
};

/* Red / amber notice inside the billing strip when the governing plan
   is not healthy. Reads the server's ``plan.state`` verdict so it can't
   disagree with what the API enforces. */
const PlanStateNotice = ({ state, onChangePlan }) => {
  const theme = useTheme();
  if (!state || state.status === 'active') return null;
  const tone = state.status === 'expiring_soon' ? 'warning' : 'error';
  const color = theme.palette[tone].main;
  const graceLabel = state.grace_ends_at
    ? new Date(state.grace_ends_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  let extra = null;
  if (state.status === 'expired' && graceLabel) {
    extra = state.effect === 'read_only'
      ? `Scheduled goals, pipelines and deployments will be paused on ${graceLabel} unless the plan is renewed.`
      : `Resources above the Free-plan limits will be paused on ${graceLabel} unless the plan is renewed.`;
  } else if (state.status === 'suspended') {
    extra = 'Paused goals, pipelines and deployments resume automatically once the plan is renewed.';
  } else if (state.status === 'expiring_soon') {
    extra = state.effect === 'read_only' || state.tier === 'team' || state.tier === 'enterprise'
      ? 'Renew before then to keep write access and running automations.'
      : 'Renew before then to keep your current limits.';
  }
  return (
    <Alert
      severity={tone}
      variant="outlined"
      icon={false}
      sx={{
        mt: 1.25, py: 0.5, px: 1.25, borderRadius: 1.5,
        borderColor: alpha(color, 0.5),
        backgroundColor: alpha(color, 0.06),
        '& .MuiAlert-message': { width: '100%', py: 0.25 },
        '& .MuiAlert-action': { pt: 0, alignItems: 'center' },
      }}
      action={(
        <CMButtonOutlinedSmall onClick={onChangePlan} sx={{ height: 26, whiteSpace: 'nowrap' }}>
          {state.status === 'none' ? 'Choose a plan' : 'Renew plan'}
        </CMButtonOutlinedSmall>
      )}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
        {state.message}
      </Typography>
      {extra && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
          {extra}
        </Typography>
      )}
    </Alert>
  );
};

// ----- shared visual helpers (goal-page vocabulary) --------------------------

/* Surface header — 28px tinted icon box + 10px overline, optional
   "Open"-style caption action on the right. Identical to the header
   pattern in GoalDashboard's Forecast / Serving / Resources cards. */
const SurfaceHeader = ({ icon, label, action, actionLabel, tone = 'primary', sx }) => {
  const theme = useTheme();
  const color = theme.palette[tone]?.main || theme.palette.primary.main;
  return (
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5, ...sx }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{
          width: 28, height: 28, borderRadius: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(color, 0.12),
          color,
        }}>
          {icon}
        </Box>
        <Typography variant="overline" sx={{ fontSize: 10, letterSpacing: 1, fontWeight: 600, color: 'text.secondary' }}>
          {label}
        </Typography>
      </Stack>
      {action && (
        <Typography
          variant="caption"
          onClick={action}
          sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
        >
          {actionLabel || 'Open'}
        </Typography>
      )}
    </Stack>
  );
};

const MeterRow = ({ meter }) => {
  const pct = meter.included
    ? Math.min(100, (meter.used / meter.included) * 100)
    : (meter.used > 0 ? 100 : 0);
  const tone = meterTone(meter.used, meter.included);
  const bgColor = tone === 'error' ? 'error.main'
    : tone === 'warning' ? 'warning.main' : 'success.main';
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>
          {meter.label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {meter.used.toFixed(
            meter.unit === 'GB-days' || meter.unit === 'pod-days' ? 1 : 2,
          )}
          {' / '}
          {meter.included === 0 ? '—' : meter.included.toLocaleString()}
          {' '}{meter.unit}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 8, borderRadius: 1,
          bgcolor: (t) => alpha(t.palette.text.primary, 0.06),
          '& .MuiLinearProgress-bar': { bgcolor: bgColor },
        }}
      />
      {meter.overage > 0 && (
        <Typography variant="caption" color="error.main" sx={{ mt: 0.5, display: 'block' }}>
          Over included quota by {meter.overage.toFixed(2)} {meter.unit}
        </Typography>
      )}
    </Box>
  );
};

const QuotaRow = ({ name, used, limit, label }) => (
  <Box>
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
      <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {formatLimitUsage(used, limit)}
      </Typography>
    </Stack>
    <LinearProgress
      variant="determinate"
      value={limitPercent(used, limit)}
      sx={{
        height: 6, borderRadius: 1,
        bgcolor: (t) => alpha(t.palette.text.primary, 0.06),
      }}
    />
  </Box>
);

// Plain scalar quotas. Everything except "Workspaces" is a
// per-workspace cap (usage shown for the scoped workspace).
// Deployments are rendered out-of-band below because they have two
// distinct caps (public/shared vs dedicated).
const QUOTA_DISPLAY = [
  { key: 'workspaces',           usageKey: 'workspaces',         label: 'Workspaces (account)' },
  { key: 'segments',           usageKey: 'segments',           label: 'Segments' },
  { key: 'models',             usageKey: 'models',             label: 'Models' },
  { key: 'pipelines',          usageKey: 'pipelines',          label: 'Pipelines' },
  { key: 'goals',              usageKey: 'goals',              label: 'Goals' },
  { key: 'predictfm_adapters', usageKey: 'predictfm_adapters', label: 'Foundation adapters' },
  { key: 'byom_models',        usageKey: 'byom_models',        label: 'BYOM models' },
  { key: 'streams',            usageKey: 'streams',            label: 'Event streams' },
  { key: 'signals',            usageKey: 'signals',            label: 'Signals' },
];

// ``deployments`` is a flat scalar cap (all deployments are treated
// the same - no priority tiers).
const deploymentCap = (limit) => {
  const n = Number(limit);
  return Number.isFinite(n) ? n : 0;
};

// ----- the page itself -----------------------------------------------------

const BillingSection = ({
  sub,
  snapshot, snapshotLoading, snapshotError,
  transactionsResponse, workspacesResponse,
  loadSnapshot, loadBalance, loadTransactions, loadSubscription,
  loadWorkspaces,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  // Role gate for org-billed accounts: only the org's owner, admins
  // and billing managers may see billing surfaces. Personal (user-
  // billed) accounts are always accessible to their owner.
  const viewerOrgs = useViewerOrganizations();
  const section = SUBS.includes(sub) ? sub : 'overview';

  const [topupOpen, setTopupOpen] = useState(false);
  const [plansOpen, setPlansOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [activityFilter, setActivityFilter] = useState('');
  // '' = all workspaces; otherwise a workspace uid. Applies to every
  // transaction-derived view via the server-side
  // reference.workspace_id filter.
  const [workspaceFilter, setWorkspaceFilter] = useState('');
  const [page, setPage] = useState(1);

  // QUOTA MODEL: every limit and monthly meter except seats/workspaces
  // is a PER-WORKSPACE allowance, so the snapshot's limits / usage /
  // meters / ai_usage blocks are always scoped to ONE workspace — the
  // selected filter, or the active workspace when the ledger filter is
  // on "All workspaces".
  const { activeWorkspaceId } = useWorkspace();
  const scopeWorkspaceId = workspaceFilter || activeWorkspaceId || '';

  useEffect(() => {
    loadBalance();
    loadSubscription();
    loadWorkspaces();
  }, [loadBalance, loadSubscription, loadWorkspaces]);

  // Return from Stripe Checkout. The success/cancel URLs (STRIPE_CHECKOUT_*_URL
  // on the api-server) land here with ?checkout=success|cancel. Stripe's
  // webhook (invoice.paid / checkout.session.completed) is what actually
  // grants credits / flips the plan and it typically arrives a second or two
  // after the redirect. The mount effects above already fetch once; on
  // success we re-pull the billing state again at +3s and +8s so the new
  // balance / plan shows up without a manual refresh. The query string is
  // consumed (replaced) so a reload doesn't re-trigger the banner. Mount-only
  // on purpose: re-running on searchParams change would cancel the timers.
  const [searchParams, setSearchParams] = useSearchParams();
  const [checkoutOutcome, setCheckoutOutcome] = useState(null);
  useEffect(() => {
    const outcome = searchParams.get('checkout');
    if (outcome !== 'success' && outcome !== 'cancel') return undefined;
    setCheckoutOutcome(outcome);
    const next = new URLSearchParams(searchParams);
    next.delete('checkout');
    next.delete('session_id');
    setSearchParams(next, { replace: true });
    if (outcome !== 'success') return undefined;
    const refetch = () => {
      loadBalance();
      loadSubscription();
      loadSnapshot({ workspace_id: scopeWorkspaceId || undefined });
      loadTransactions({ limit: PAGE_SIZE, offset: 0, workspace_id: workspaceFilter || undefined });
    };
    const timers = [3000, 8000].map((ms) => setTimeout(refetch, ms));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ?plans=1 (from the global plan banner / expired-plan dialog) opens the
  // plan chooser straight away; consumed so a reload doesn't reopen it.
  useEffect(() => {
    if (searchParams.get('plans') !== '1') return;
    setPlansOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('plans');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadTransactions({
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      workspace_id: workspaceFilter || undefined,
    });
  }, [loadTransactions, page, workspaceFilter]);

  const transactions = transactionsResponse?.transactions || [];
  const workspaces = workspacesResponse?.workspaces || [];

  // Wider transaction window for the charts; snapshot re-fetched too so
  // its per-workspace blocks (limits/usage/meters/ai_usage) track the
  // scoped workspace.
  useEffect(() => {
    loadTransactions({ limit: 500, offset: 0, workspace_id: workspaceFilter || undefined });
    loadSnapshot({ workspace_id: scopeWorkspaceId || undefined });
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceFilter, scopeWorkspaceId]);

  const filteredTxns = useMemo(() => {
    if (!activityFilter) return transactions;
    return transactions.filter((t) => describeActivity(t.reason).category === activityFilter);
  }, [transactions, activityFilter]);

  const balance     = snapshot?.balance;
  const meters      = snapshot?.meters || [];
  const limits      = snapshot?.limits || {};
  const usage       = snapshot?.usage || {};

  const periodSpend = useMemo(
    () => (transactions || []).reduce((s, t) => {
      if (!t?.amount || t.amount >= 0) return s;
      const cat = describeActivity(t.reason).category;
      return isSpendCategory(cat) ? s + (-t.amount) : s;
    }, 0),
    [transactions],
  );

  if (snapshotLoading && !snapshot) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  }
  if (snapshotError && !snapshot) {
    return <Alert severity="error">Failed to load billing: {String(snapshotError)}</Alert>;
  }

  // Org-billed account + caller's role in that org lacks billing
  // access (member/viewer/publisher) -> restricted panel instead of
  // the billing surfaces.
  if (balance?.scope === 'org') {
    const role = viewerOrgs.find((o) => o.uid === balance.uid)?.role || null;
    if (!['owner', 'admin', 'billing'].includes(role)) {
      return (
        <Alert severity="info">
          Billing for this organization is restricted to its owner,
          admins and billing managers. Ask an organization admin if you
          need access — they can give you the <strong>billing</strong>{' '}
          role from the Organization settings.
        </Alert>
      );
    }
  }

  const balanceCredits = balance?.current_balance ?? 0;
  const planState = snapshot?.plan?.state || null;
  // A lapsed plan outranks balance health for the strip tone: plenty of
  // credits mean nothing if the org is read-only.
  const planTone = !planState || planState.status === 'active' ? null
    : planState.status === 'expiring_soon' ? 'warning' : 'error';
  const planLabel = planState?.status === 'expiring_soon' ? 'Plan ending soon'
    : planState?.status === 'expired' ? 'Plan expired'
    : planState?.status === 'suspended' ? 'Plan suspended'
    : planState?.status === 'none' ? 'No active plan' : null;
  const balanceHealthInfo = balanceHealth(balanceCredits);
  const balanceTone = planTone === 'error' ? 'error'
    : planTone === 'warning' && balanceHealthInfo.tone === 'success' ? 'warning'
    : balanceHealthInfo.tone;
  const balanceLabel = planTone === 'error' ? planLabel
    : planTone === 'warning' && balanceHealthInfo.tone === 'success' ? planLabel
    : balanceHealthInfo.label;

  const refreshAll = () => {
    loadSnapshot({ workspace_id: scopeWorkspaceId || undefined });
    loadBalance();
    loadSubscription();
  };

  const scopeWorkspaceName = workspaces.find(
    (w) => w.uid === scopeWorkspaceId,
  )?.name || scopeWorkspaceId;

  // ===== Balance strip — GoalSummaryStrip styling + billing actions ========

  const toneColor = theme.palette[balanceTone]?.main || theme.palette.info.main;

  const BalanceStrip = (
    <Paper
      variant="outlined"
      sx={{
        mb: 2, p: 1.5, borderRadius: 2,
        borderLeft: `3px solid ${toneColor}`,
        backgroundColor: alpha(toneColor, 0.04),
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.25}
        alignItems={{ sm: 'center' }}
        sx={{ minWidth: 0 }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{
            width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: alpha(toneColor, 0.12),
          }}>
            <Box sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: toneColor }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: toneColor, fontWeight: 700, lineHeight: 1.2 }}>
              {balanceLabel}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.25 }} noWrap>
              <Box component="span" sx={{ fontWeight: 700 }}>
                {formatCredits(balanceCredits)} credits
              </Box>
              {' '}· {formatCredits(periodSpend)} spent this period
              {' '}· {formatCredits(balance?.total_granted)} granted lifetime
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }} noWrap>
              <PlanMetaLine />
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 1 }}>
          <Tooltip title="Refresh" arrow>
            <IconButton size="small" onClick={refreshAll}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <StripePortalLink size="small" variant="text" label="Manage billing" />
          <CMButtonOutlinedSmall
            startIcon={<RocketLaunchIcon sx={{ fontSize: 14 }} />}
            onClick={() => setPlansOpen(true)}
          >
            Change plan
          </CMButtonOutlinedSmall>
          <CMButton
            startIcon={<AddCircleOutlineIcon sx={{ fontSize: 16 }} />}
            onClick={() => setTopupOpen(true)}
            sx={{ height: 30, fontSize: 12 }}
          >
            Top up
          </CMButton>
        </Stack>
      </Stack>
      <PlanStateNotice state={planState} onChangePlan={() => setPlansOpen(true)} />
    </Paper>
  );

  // ===== Section panels =====================================================

  const inferencesThisPeriod = snapshot?.ai_usage?.inference?.count || 0;
  const trainingsThisPeriod  = snapshot?.ai_usage?.training?.count  || 0;
  const ingestGbThisPeriod   = (snapshot?.meters || [])
    .find((m) => m.meter === 'ingest_gb')?.used || 0;

  const OverviewPanel = (
    // useFlexGap: without it Stack resets child margins, which breaks the
    // negative-margin alignment of direct <Grid container> children.
    <Stack spacing={2} useFlexGap>
      {/* ── KPI tiles — same primitives as the goal dashboard. Inference,
          training and ingestion figures are PER WORKSPACE (the scoped
          one); spend is the account wallet. ─────────────────────────── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 2 }}>
        <KPICard
          tone="primary"
          icon={<PaidOutlinedIcon sx={{ fontSize: 18 }} />}
          label="Spend this period"
          primary={formatCredits(periodSpend)}
          subtitle="credits"
          onShortcut={() => navigate('/settings/billing/activity')}
          shortcutTitle="Open Activity"
        />
        <KPICard
          tone={inferencesThisPeriod > 0 ? 'success' : 'neutral'}
          icon={<InferencesIcon sx={{ fontSize: 18 }} />}
          label="Inferences (workspace)"
          primary={formatNumber(inferencesThisPeriod)}
          subtitle={`${formatCredits(snapshot?.ai_usage?.inference?.credits_spent || 0)} credits spent`}
          onShortcut={() => navigate('/settings/billing/usage')}
          shortcutTitle="Open Usage"
        />
        <KPICard
          tone={trainingsThisPeriod > 0 ? 'success' : 'neutral'}
          icon={<ModelTrainingIcon sx={{ fontSize: 18 }} />}
          label="Trainings (workspace)"
          primary={formatNumber(trainingsThisPeriod)}
          subtitle={`${formatCredits(snapshot?.ai_usage?.training?.credits_spent || 0)} credits spent`}
          onShortcut={() => navigate('/settings/billing/usage')}
          shortcutTitle="Open Usage"
        />
        <KPICard
          tone={ingestGbThisPeriod > 0 ? 'success' : 'neutral'}
          icon={<CloudUploadIcon sx={{ fontSize: 18 }} />}
          label="Data ingested (workspace)"
          primary={ingestGbThisPeriod.toFixed(2)}
          subtitle="GB this period"
          onShortcut={() => navigate('/settings/billing/usage')}
          shortcutTitle="Open Usage"
        />
      </Box>

      {/* ── Spend visuals ───────────────────────────────────────────────── */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Surface sx={{ p: 2.5, height: '100%' }}>
            <SurfaceHeader
              icon={<ShowChartIcon sx={{ fontSize: 18 }} />}
              label="Daily spend (30 days)"
            />
            <SpendChart transactions={transactions} />
          </Surface>
        </Grid>
        <Grid item xs={12} md={4}>
          <Surface sx={{ p: 2.5, height: '100%' }}>
            <SurfaceHeader
              icon={<DonutSmallIcon sx={{ fontSize: 18 }} />}
              label="Spend by category"
            />
            <CategoryDonut transactions={transactions} />
          </Surface>
        </Grid>
      </Grid>

      {/* ── Recent activity ─────────────────────────────────────────────── */}
      <Surface sx={{ p: 2.5 }}>
        <SurfaceHeader
          icon={<HistoryIcon sx={{ fontSize: 18 }} />}
          label="Recent activity"
          action={() => navigate('/settings/billing/activity')}
        />
        <ActivityTable
          transactions={transactions.slice(0, 8)}
          empty="No activity yet — start by training a model or uploading data."
          compact
        />
      </Surface>
    </Stack>
  );

  const UsagePanel = (
    <Stack spacing={2} useFlexGap>
      <Grid container spacing={2}>
        {/* DATA - included monthly meters */}
        <Grid item xs={12} md={6}>
          <Surface sx={{ p: 2.5, height: '100%' }}>
            <SurfaceHeader
              icon={<SpeedIcon sx={{ fontSize: 18 }} />}
              label="Data this period"
              action={() => setPricingOpen(true)}
              actionLabel="Pricing"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Every workspace gets its own monthly included allowance — these
              bars show <strong>{scopeWorkspaceName || 'this workspace'}</strong>&apos;s
              usage this period. Usage above the included amount is charged
              at the per-unit overage rate.
            </Typography>
            <Stack spacing={2.5}>
              {meters.map((m) => <MeterRow key={m.meter} meter={m} />)}
            </Stack>
          </Surface>
        </Grid>

        {/* AI OPS - pay-as-you-go */}
        <Grid item xs={12} md={6}>
          <Surface sx={{ p: 2.5, height: '100%' }}>
            <SurfaceHeader
              icon={<MemoryIcon sx={{ fontSize: 18 }} />}
              label="AI operations"
            />
            <AIOpsPanel aiUsage={snapshot?.ai_usage} />
          </Surface>
        </Grid>
      </Grid>

      {/* SPEND BY WORKSPACE */}
      <Surface sx={{ p: 2.5 }}>
        <SurfaceHeader
          icon={<WorkspacesIcon sx={{ fontSize: 18 }} />}
          label="Spend by workspace"
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Credits spent per workspace across the recent transaction window,
          stacked by category. Account-level charges that aren&apos;t tied to a
          workspace appear as &quot;Unattributed&quot;.
        </Typography>
        <WorkspaceSpendChart transactions={transactions} workspaces={workspaces} />
      </Surface>

      {/* PLAN HARD QUOTAS */}
      <Surface sx={{ p: 2.5 }}>
        <SurfaceHeader
          icon={<RuleIcon sx={{ fontSize: 18 }} />}
          label="Plan quotas"
          action={() => setPlansOpen(true)}
          actionLabel="Change plan"
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Hard limits enforced at create time — going over rejects the request;
          credits are never consumed by a blocked request. Every quota except
          seats and workspaces is per workspace: the bars show{' '}
          <strong>{scopeWorkspaceName || 'this workspace'}</strong>&apos;s live
          usage against its own allowance.
        </Typography>
        <Grid container spacing={2}>
          {QUOTA_DISPLAY.map((q) => (
            <Grid item xs={12} sm={6} md={4} key={q.key}>
              <QuotaRow
                name={q.key}
                label={q.label}
                used={usage[q.usageKey]}
                limit={limits[q.key]}
              />
            </Grid>
          ))}

          {/* Shared / Dedicated deployment tiles, shown against the
              matching usage bucket. */}
          {(() => {
            const sharedLim = deploymentCap(limits.deployments);
            const sharedUsed = usage.deployments_public ?? 0;
            if (sharedLim <= 0 && sharedUsed <= 0) return null;
            return (
              <Grid item xs={12} sm={6} md={4} key="shared-deployments">
                <QuotaRow
                  name="shared_deployments"
                  label="Shared deployments"
                  used={sharedUsed}
                  limit={sharedLim}
                />
              </Grid>
            );
          })()}
          <Grid item xs={12} sm={6} md={4} key="dedicated-deployments">
            <QuotaRow
              name="dedicated_deployments"
              label="Dedicated deployments"
              used={usage.deployments_dedicated}
              limit={limits.dedicated_deployments}
            />
          </Grid>
        </Grid>
      </Surface>

      {/* Full price list lives in a dialog — not a page. */}
      <Stack direction="row" justifyContent="center" sx={{ pt: 0.5 }}>
        <CMButtonOutlinedSmall
          startIcon={<PaidOutlinedIcon sx={{ fontSize: 15 }} />}
          onClick={() => setPricingOpen(true)}
        >
          View pricing
        </CMButtonOutlinedSmall>
      </Stack>
    </Stack>
  );

  const ActivityPanel = (
    <Surface sx={{ p: 2.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <SurfaceHeader
          icon={<HistoryIcon sx={{ fontSize: 18 }} />}
          label="Activity history"
          sx={{ mb: 0 }}
        />
        <TextField
          select size="small" label="Filter"
          value={activityFilter}
          onChange={(e) => setActivityFilter(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          {FILTER_OPTIONS.map((opt) => (
            <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
          ))}
        </TextField>
      </Stack>

      <ActivityTable
        transactions={filteredTxns}
        empty="No matching activity. Try a different filter."
      />

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Pagination
          page={page}
          count={Math.max(page, transactions.length === PAGE_SIZE ? page + 1 : page)}
          onChange={(_, p) => setPage(p)}
          size="small"
        />
      </Box>
    </Surface>
  );

  const showWorkspaceFilter = section !== 'invoices';

  return (
    <Box sx={{ width: '100%' }}>
      {checkoutOutcome === 'success' && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCheckoutOutcome(null)}>
          Payment received. Your balance and plan update as soon as Stripe
          confirms the payment — usually within a few seconds. Use the refresh
          button if you don&apos;t see the change.
        </Alert>
      )}
      {checkoutOutcome === 'cancel' && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setCheckoutOutcome(null)}>
          Checkout was cancelled. Nothing was charged.
        </Alert>
      )}

      {/* At-a-glance strip + primary actions — lives on every
          section, the way GoalSummaryStrip anchors the goal panel. */}
      {BalanceStrip}

      {/* Workspace scope. The ledger views ('' = all workspaces) AND the
          per-workspace quota/meter/usage blocks follow this selector —
          when it's on "All workspaces" the quota blocks fall back to the
          active workspace (quotas are per-workspace, there is no
          account-wide gauge to show). */}
      {showWorkspaceFilter && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
          <TextField
            select size="small" label="Workspace"
            value={workspaceFilter}
            onChange={(e) => setWorkspaceFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All workspaces (ledger)</MenuItem>
            {workspaces.map((w) => (
              <MenuItem key={w.uid} value={w.uid}>{w.name || w.uid}</MenuItem>
            ))}
          </TextField>
        </Stack>
      )}

      {workspaceFilter && showWorkspaceFilter && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing spend, activity, quotas and meters for{' '}
          <strong>
            {workspaces.find((w) => w.uid === workspaceFilter)?.name || workspaceFilter}
          </strong>
          {' '}only. Account-level rows (top-ups, grants) and the wallet
          balance stay account-wide.
        </Alert>
      )}

      {section === 'overview' && OverviewPanel}
      {section === 'usage' && UsagePanel}
      {section === 'activity' && ActivityPanel}
      {section === 'invoices' && <InvoicesPanel />}

      <TopUpDialog open={topupOpen} onClose={() => setTopupOpen(false)} />

      {/* Plan chooser — an action dialog, not a page. */}
      <Dialog
        open={plansOpen}
        onClose={() => setPlansOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <RocketLaunchIcon color="primary" sx={{ fontSize: 20 }} />
            <Typography variant="h6" fontWeight={700}>Change plan</Typography>
          </Stack>
          <IconButton
            onClick={() => setPlansOpen(false)}
            size="small"
            sx={{ position: 'absolute', top: 12, right: 12 }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <PlansPanel snapshot={snapshot} />
        </DialogContent>
      </Dialog>

      {/* Full price list — an on-demand dialog, not a page. */}
      <Dialog
        open={pricingOpen}
        onClose={() => setPricingOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PaidOutlinedIcon color="primary" sx={{ fontSize: 20 }} />
            <Typography variant="h6" fontWeight={700}>Pricing</Typography>
          </Stack>
          <IconButton
            onClick={() => setPricingOpen(false)}
            size="small"
            sx={{ position: 'absolute', top: 12, right: 12 }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {pricingOpen && <PricingTable />}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

// ----- shared activity table ----------------------------------------------

const ActivityTable = ({ transactions, empty, compact = false }) => (
  <Box sx={{ overflowX: 'auto' }}>
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell sx={{ fontWeight: 600 }}>When</TableCell>
        <TableCell sx={{ fontWeight: 600 }}>Activity</TableCell>
        {!compact && <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>}
        <TableCell sx={{ fontWeight: 600 }} align="right">Amount</TableCell>
        {!compact && <TableCell sx={{ fontWeight: 600 }} align="right">Balance after</TableCell>}
      </TableRow>
    </TableHead>
    <TableBody>
      {transactions.length === 0 && (
        <TableRow>
          <TableCell colSpan={compact ? 3 : 5} align="center">
            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
              {empty}
            </Typography>
          </TableCell>
        </TableRow>
      )}
      {transactions.map((txn) => {
        const meta = describeActivity(txn.reason);
        const color = CATEGORY_COLORS[meta.category] || '#94a3b8';
        const isCredit = txn.amount > 0;
        return (
          <TableRow key={txn.txn_id} hover>
            <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
              {new Date(txn.ts).toLocaleString(undefined, {
                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
              })}
            </TableCell>
            <TableCell>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />
                <Typography variant="body2">{meta.label}</Typography>
              </Stack>
            </TableCell>
            {!compact && (
              <TableCell>
                <Chip size="small" label={meta.category}
                      sx={{ bgcolor: alpha(color, 0.12), color, fontWeight: 500 }} />
              </TableCell>
            )}
            <TableCell align="right" sx={{
              color: isCredit ? 'success.main' : 'error.main',
              fontWeight: 600, whiteSpace: 'nowrap',
            }}>
              {isCredit ? '+' : ''}{formatCredits(txn.amount)}
            </TableCell>
            {!compact && (
              <TableCell align="right" sx={{ color: 'text.secondary' }}>
                {formatCredits(txn.balance_after)}
              </TableCell>
            )}
          </TableRow>
        );
      })}
    </TableBody>
  </Table>
  </Box>
);

const mapStateToProps = (state) => ({
  snapshot:        state.billing_snapshot.response,
  snapshotLoading: state.billing_snapshot.isLoading,
  snapshotError:   state.billing_snapshot.error,
  transactionsResponse: state.billing_transactions.response,
  workspacesResponse:   state.workspace_list.response,
});

const mapDispatchToProps = {
  loadSnapshot:     fetchSnapshot,
  loadBalance:      fetchBalance,
  loadTransactions: fetchTransactions,
  loadSubscription: fetchSubscription,
  loadWorkspaces:   fetchWorkspaces,
};

export default connect(mapStateToProps, mapDispatchToProps)(BillingSection);
